import { useState } from "react";
import classNames from "classnames";
import { Button, Flex, Image, Text, Icon } from "@simplybusiness/mobius";
import { lock } from "@simplybusiness/icons";
import { CardPopover } from "../../../QuoteCards/QuoteCard/InfoModal/CardPopover";
import {
  PaymentTermsChecklist,
  type PaymentTermsOption,
} from "../PaymentTermsChecklist";

export const CallToAction = ({
  enableCheckoutFormSubmission,
  isFormSubmitting,
  fireCheckoutButtonClickedEvent,
  onCallToActionClick,
  buttonText,
  poweredByStripeLogo,
  cancelAnytimeExperimentEnabled,
  agreementOptions,
  errorMessage = "Please accept all terms to continue",
  eventTrackerCallback,
  legalAgreementButtonsNotCheckedCallback,
}: CallToActionProps) => {
  const [checkedAgreements, setCheckedAgreements] = useState<string[]>([]);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const allAccepted = agreementOptions.every(opt =>
    checkedAgreements.includes(opt.value),
  );
  const showError = hasAttemptedSubmit && !allAccepted;

  const handleCancelAnytimeClick = () => {
    eventTrackerCallback();
  };

  const handleCheckoutClick = (e: React.MouseEvent) => {
    setHasAttemptedSubmit(true);
    if (!allAccepted) {
      e.preventDefault();
      legalAgreementButtonsNotCheckedCallback();
      return;
    }
    fireCheckoutButtonClickedEvent();
  };

  const handlePayClick = () => {
    setHasAttemptedSubmit(true);
    if (!allAccepted) {
      legalAgreementButtonsNotCheckedCallback();
      return;
    }
    onCallToActionClick(true);
  };

  return (
    <div
      className={classNames("mobius-payment-summary__form-actions-box", {
        "combined-payment-page-sticky": !enableCheckoutFormSubmission,
      })}
    >
      <PaymentTermsChecklist
        id="checklist"
        options={agreementOptions}
        value={checkedAgreements}
        onChange={setCheckedAgreements}
        isInvalid={showError}
        errorMessage={showError ? errorMessage : undefined}
      />
      <Flex
        data-testid="payment-cta-container"
        className={
          cancelAnytimeExperimentEnabled
            ? "mobius-payment-cta-container-align-left"
            : "mobius-payment-cta-container"
        }
      >
        {cancelAnytimeExperimentEnabled && (
          <div className="mobius-payment-cta__popover-wrapper">
            <CardPopover
              toggleText="Cancel anytime"
              body="You can easily cancel your insurance policy at any time during the policy period. Please note that penalties may apply."
              onOpen={handleCancelAnytimeClick}
            />
          </div>
        )}
        {enableCheckoutFormSubmission ? (
          <Button
            className="mobius-payment-summary__checkout-button"
            type="submit"
            size="md"
            isLoading={isFormSubmitting}
            isDisabled={isFormSubmitting}
            data-testid="submit-btn"
            onClick={handleCheckoutClick}
          >
            Checkout
          </Button>
        ) : (
          <Flex flexDirection="column">
            <Button
              className="mobius-payment-summary__checkout-button combined-payment-page-sticky"
              size="md"
              data-testid="open-payment-drawer-btn"
              onClick={handlePayClick}
            >
              {buttonText}
            </Button>
            <Flex
              flexDirection="row"
              alignItems="center"
              justifyContent="center"
            >
              <Icon
                icon={lock}
                className="mobius-call-to-action_secure-checkout-icon"
              />
              <Text className="mobius-call-to-action_secure-checkout-text">
                Secure Checkout{" "}
              </Text>
              <Image
                className="powered-by-stripe-logo"
                src={poweredByStripeLogo}
                alt="Powered by Stripe"
              />
            </Flex>
          </Flex>
        )}
      </Flex>
    </div>
  );
};

export interface CallToActionProps {
  enableCheckoutFormSubmission: boolean;
  isFormSubmitting: boolean;
  fireCheckoutButtonClickedEvent: () => void;
  onCallToActionClick: (drawerOpen: boolean) => void;
  buttonText: string;
  poweredByStripeLogo: string;
  cancelAnytimeExperimentEnabled: boolean;
  agreementOptions: PaymentTermsOption[];
  errorMessage?: string;
  eventTrackerCallback: () => void;
  legalAgreementButtonsNotCheckedCallback: () => void;
}
